import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowLeftIcon } from "assets/images/arrow-left.svg";

import { ReactComponent as ChevronDownIcon } from "assets/images/chevron-down-new.svg";

import Title from "components/atoms/Title";
import {
  useGetAuthenticatedUserQuery,
  useGetDashboardQuery,
} from "generated/graphql";
import { useHistory } from "react-router-dom";
import EssentialsPlanCard from "components/molecules/PaidPlanCards/EssentialsPlanCard";
import ClarityPlanCard from "components/molecules/PaidPlanCards/ClarityPlanCard";
import GuidancePlanCard from "components/molecules/PaidPlanCards/GuidancePlanCard";
import { UserPlan } from "types/user";
import {
  Container,
  Muted,
  Header,
  BackButton,
  HeaderTitle,
  WelcomeSection,
  DownloadButton,
  MainContent,
  InterestInventorySection,
  SectionTitle,
  ChartContainer,
  BarChartWrapper,
  CategoryLabelsWrapper,
  BarItem,
  BarFill,
  BarValue,
  CategoryLabel,
  BooksIconWrapper,
  DetailsButton,
  InfoIcon,
  AptitudeSection,
  PlaceholderCard,
  PlaceholderContent,
  PlaceholderTitle,
  PlaceholderText,
  AssessmentsButton,
  DetailsText,
  PricingCardsSection,
  MobileInterestChartWrapper,
  ShowMoreButton,
  ShowMoreButtonWrapper,
  ChevronDown,
  MobileInterestWrapper,
  PricingCardsHeader,
  InterestModal,
  InterestModalContent,
  InterestItem,
} from "./styles";
import { DesktopOnly, MobileOnly } from "../Dashboard/styles";

const InterestInventoryResults = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [interestInventory, setInterestInventory] = useState<any[]>([]);

  const [showAllInterests, setShowAllInterests] = useState(false);

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const interestInventoryToShow = useMemo(() => {
    return [...interestInventory]?.sort((a, b) => b.score - a.score);
  }, [interestInventory]);

  const mobileInterests = useMemo(() => {
    if (showAllInterests) {
      return interestInventoryToShow;
    }
    return [...interestInventoryToShow].splice(0, 6);
  }, [showAllInterests, interestInventoryToShow]);

  const handleShowAllInterests = () => {
    setShowAllInterests((prev) => !prev);
  };

  const handleDownloadDashboard = () => {
    // Handle dashboard download
    window.print();
  };

  const handleDetailsClick = () => {
    setIsDetailsModalOpen(true);
  };

  const handleGoToAssessments = () => {
    // Handle navigation to assessments
    history.push(`/student`);
  };

  const handleGoToPayments = (type: string) => {
    // Handle navigation to payments
    history.push(`/individual/payment/${type}`);
  };

  const {
    data: userData,
    loading: userDataLoading,
  } = useGetAuthenticatedUserQuery();

  const { data: dashboardData } = useGetDashboardQuery({
    variables: {
      testId:
        Number(userData?.authenticatedItem?.students?.[0]?.testAttempt?.id) ||
        -1,
    },
  });

  const userPlan = useMemo(() => {
    if (userData && !userDataLoading) {
      return userData?.authenticatedItem?.plan || undefined;
    }
    return null;
  }, [userData, userDataLoading]);

  const showEssentialsPricePlan = useMemo(() => {
    if (userPlan === null) {
      return false;
    }
    return userPlan === UserPlan.free || userPlan === undefined;
  }, [userPlan]);

  const showClarityPricePlan = useMemo(() => {
    if (userPlan === null) {
      return false;
    }
    return (
      userPlan === undefined ||
      [UserPlan.free, UserPlan.essentials].includes(userPlan as UserPlan)
    );
  }, [userPlan]);

  const showGuidancePricePlan = useMemo(() => {
    if (userPlan === null) {
      return false;
    }
    return (
      userPlan === undefined ||
      [UserPlan.free, UserPlan.essentials, UserPlan.clarity].includes(
        userPlan as UserPlan
      )
    );
  }, [userPlan]);

  const priceForClarity = useMemo(() => {
    if (userPlan === "free") {
      return 195;
    }

    if (userPlan === "essentials") {
      return 130;
    }

    return 195;
  }, [userPlan]);

  const priceForGuidance = useMemo(() => {
    if (userPlan === "free") {
      return 315;
    }

    if (userPlan === "essentials" || userPlan === "clarity") {
      return 250;
    }

    return 315;
  }, [userPlan]);

  useEffect(() => {
    if (dashboardData?.getDashboard?.interestInventory) {
      setInterestInventory(dashboardData?.getDashboard?.interestInventory);
    }
  }, [dashboardData]);

  return (
    <Container>
      <InterestModal
        isOpen={isDetailsModalOpen}
        title="Interest Inventory"
        onClose={() => setIsDetailsModalOpen(false)}
      >
        <InterestModalContent>
          {interestInventoryToShow.map((item) => (
            <InterestItem key={item.id}>
              <p>
                <strong>{item.name}</strong>
                {item.description}
              </p>
            </InterestItem>
          ))}
        </InterestModalContent>
      </InterestModal>
      <Header>
        <BackButton onClick={handleGoToAssessments}>
          <ArrowLeftIcon />
        </BackButton>
        <HeaderTitle onClick={handleGoToAssessments}>
          GO TO YOUR ASSESSMENTS
        </HeaderTitle>
      </Header>
      <WelcomeSection>
        <Title style={{ marginBottom: "0" }}>
          <Muted>{t("user.hi")},</Muted>{" "}
          {userData?.authenticatedItem?.firstName || ""}!
        </Title>
        <DownloadButton variant="secondary" onClick={handleDownloadDashboard}>
          Download dashboard
        </DownloadButton>
      </WelcomeSection>
      <MainContent>
        <InterestInventorySection>
          <div>
            <SectionTitle>INTEREST INVENTORY</SectionTitle>
            <DetailsButton onClick={handleDetailsClick}>
              <InfoIcon />
              <DetailsText>Details</DetailsText>
            </DetailsButton>
          </div>

          <ChartContainer>
            <DesktopOnly>
              <BarChartWrapper>
                {interestInventoryToShow.map((item, index) => (
                  <BarItem key={index}>
                    <BarFill score={item.score} />
                    <BarValue>{item.score}</BarValue>
                  </BarItem>
                ))}
              </BarChartWrapper>

              <CategoryLabelsWrapper>
                {interestInventoryToShow.map((item, index) => (
                  <CategoryLabel key={index}>
                    {item.name.toUpperCase()}
                  </CategoryLabel>
                ))}
              </CategoryLabelsWrapper>
            </DesktopOnly>
            <MobileInterestWrapper>
              {[...mobileInterests]
                ?.sort((a, b) => b.score - a.score)
                .map((item, index) => (
                  <MobileInterestChartWrapper>
                    <CategoryLabel key={index}>
                      {item.name.toUpperCase()}
                    </CategoryLabel>
                    <BarItem key={index}>
                      <BarFill score={item.score} />
                      <BarValue>{item.score}</BarValue>
                    </BarItem>
                  </MobileInterestChartWrapper>
                ))}
              <ShowMoreButtonWrapper onClick={handleShowAllInterests}>
                <ShowMoreButton>
                  {showAllInterests ? "Hide" : "Show more"}
                </ShowMoreButton>
                <ChevronDown
                  style={{
                    rotate: showAllInterests ? "180deg" : "0deg",
                    transition: "all 0.2s ease-in",
                    height: "50px",
                  }}
                />
              </ShowMoreButtonWrapper>
            </MobileInterestWrapper>
          </ChartContainer>
        </InterestInventorySection>
      </MainContent>
      {(showEssentialsPricePlan ||
        showClarityPricePlan ||
        showGuidancePricePlan) && (
        <>
          <PricingCardsHeader>{t("student.findOutMore")}</PricingCardsHeader>
          <PricingCardsSection>
            {showEssentialsPricePlan && (
              <EssentialsPlanCard
                price={65}
                handleGoToPayments={handleGoToPayments}
              />
            )}
            {showClarityPricePlan && (
              <ClarityPlanCard
                isFreeUser={showEssentialsPricePlan}
                price={priceForClarity}
                handleGoToPayments={() =>
                  handleGoToPayments(
                    showEssentialsPricePlan ? "clarity" : "clarity-add-on"
                  )
                }
              />
            )}
            {showGuidancePricePlan && (
              <GuidancePlanCard
                isFreeUser={showEssentialsPricePlan}
                price={priceForGuidance}
                handleGoToPayments={() =>
                  handleGoToPayments(
                    showEssentialsPricePlan ? "guidance" : "guidance-add-on"
                  )
                }
              />
            )}
          </PricingCardsSection>
        </>
      )}
    </Container>
  );
};

export default InterestInventoryResults;
